/**
 * Slideshow banner.
 *
 * Slides are stacked in a single grid cell rather than laid out along a wide
 * horizontal track. Only the outgoing and the incoming slide ever move, so
 * wrapping from the last slide to the first animates one step forward instead
 * of scrolling backwards past every slide in between.
 *
 * The element works without JavaScript: CSS shows the first slide, and this
 * module only takes over once it has marked the container ready.
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Distance in px a pointer must travel across the slides before it counts as a swipe. */
const SWIPE_THRESHOLD = 40;

/**
 * How long after the visitor presses on a video, its poster or its player a
 * start still counts as theirs. The theme starts a video in the same task as
 * the press, so this only has to outlast a slow device.
 */
const PRESS_WINDOW = 1000;

/**
 * How long the slideshow's own request to an embedded player to play is what
 * a start is put down to. Players take a moment to answer.
 */
const ASK_WINDOW = 5000;

/**
 * How long a click into a player that is not playing holds the slides while
 * the player works out whether it is starting. YouTube took 0.8-1.6 s to say
 * it was playing in tests.
 */
const AWAIT_WINDOW = 4000;

/** Must match the breakpoint the stylesheet hides slides and controls at. */
const MOBILE = window.matchMedia('(max-width: 749px)');

/**
 * 'youtube' or 'vimeo' for an embedded player the slideshow can pause and play
 * by message, otherwise null. YouTube only listens with its JS API switched
 * on, which the slide does for players placed inside it.
 * @param {HTMLIFrameElement} iframe
 */
function embedKind(iframe) {
  let url;
  try {
    url = new URL(iframe.src);
  } catch {
    return null;
  }
  if (/(^|\.)youtube(-nocookie)?\.com$/.test(url.hostname)) {
    return url.searchParams.get('enablejsapi') === '1' ? 'youtube' : null;
  }
  return url.hostname === 'player.vimeo.com' ? 'vimeo' : null;
}

/**
 * Whether a message from an embedded player says it started (true) or
 * stopped (false). Anything else, including YouTube's "buffering" and the
 * "unstarted" it passes through when a looping video starts over, is null.
 * @param {any} data - the parsed message
 */
function embedPlaying(data) {
  let state = null;
  if (data?.event === 'onStateChange') state = data.info;
  else if (data?.event === 'infoDelivery' && data.info && 'playerState' in data.info) state = data.info.playerState;
  if (state === 1) return true;
  if (state === 0 || state === 2) return false;
  if (data?.event === 'play') return true;
  if (data?.event === 'pause' || data?.event === 'ended') return false;
  return null;
}

/** Whether a message from a YouTube player says it is buffering, as it does the moment play is pressed. */
function embedBuffering(data) {
  if (data?.event === 'onStateChange') return data.info === 3;
  return data?.event === 'infoDelivery' && data.info?.playerState === 3;
}

/**
 * A countdown that can be paused and resumed without losing the time already
 * served, so a visitor who tabs into the slideshow and straight back out does
 * not cost the current slide a whole fresh interval.
 */
class ResumableTimer {
  #callback;
  #duration;
  #remaining;
  #startedAt = 0;
  #id = null;

  constructor(callback, duration) {
    this.#callback = callback;
    this.#duration = duration;
    this.#remaining = duration;
  }

  resume() {
    if (this.#id !== null) return;
    // A countdown that was paused right on its deadline has nothing left to
    // serve. Starting it over beats refusing to schedule and stalling.
    if (this.#remaining <= 0) this.#remaining = this.#duration;
    this.#startedAt = Date.now();
    this.#id = setTimeout(() => {
      this.#id = null;
      this.#remaining = this.#duration;
      this.#callback();
    }, this.#remaining);
  }

  /** Used when a slide asks for its own length. Takes effect on the next run. */
  setDuration(duration) {
    if (duration === this.#duration) return;
    this.#duration = duration;
    if (this.#id === null) this.#remaining = duration;
  }

  pause() {
    if (this.#id === null) return;
    clearTimeout(this.#id);
    this.#id = null;
    this.#remaining = Math.max(0, this.#remaining - (Date.now() - this.#startedAt));
  }

  stop() {
    if (this.#id !== null) clearTimeout(this.#id);
    this.#id = null;
    this.#remaining = this.#duration;
  }
}

class SlideshowBanner extends HTMLElement {
  #slides = [];
  #indicators = [];
  #current = 0;
  #timer = null;
  /**
   * Reasons autoplay is currently held: keyboard focus, a hidden tab, the
   * theme editor, or a video the visitor is watching.
   */
  #holds = new Set();
  /**
   * Videos and players in the showing slide that the visitor started and that
   * are playing now. While there is one, autoplay is held ('video').
   */
  #watching = new Set();
  /**
   * Media whose latest start was the visitor's own. They wait for the visitor
   * when their slide comes back, rather than starting again by themselves.
   */
  #startedByVisitor = new WeakSet();
  /** Videos the slideshow itself has just asked to play. */
  #startedBySlideshow = new WeakSet();
  /** Where and when the visitor last pressed inside the slideshow. */
  #lastPress = { target: null, time: 0 };
  /** What each embedded player last said: { playing, started, asked }. */
  #embeds = new WeakMap();
  /** Ends the short hold while a player the visitor clicked works out whether it plays. */
  #awaitTimer = null;
  /**
   * True once the visitor has pressed pause, and from the start for visitors
   * who asked for reduced motion. The only thing that stops autoplay outright.
   */
  #stopped = false;
  #pointerId = null;
  #pointerStart = null;
  /** True between a swipe's pointerup and the click the browser sends after it. */
  #swiped = false;
  #abort = null;
  /** The block last selected in the theme editor, so only its own deselect releases the hold. */
  #editorSelection = null;

  connectedCallback() {
    this.#slides = this.#own('[data-slideshow-slide]');
    if (this.#slides.length === 0) return;

    // A new controller on every connect: moving the element in the page
    // disconnects it, which aborts the old one and every listener with it.
    this.#abort = new AbortController();

    this.slidesEl = this.#own('[data-slideshow-slides]')[0];
    this.#indicators = this.#own('[data-slideshow-goto]');
    this.liveRegion = this.#own('[data-slideshow-live]')[0];
    this.playToggle = this.#own('[data-slideshow-toggle]')[0];
    this.progressEl = this.#own('[data-slideshow-progress]')[0];
    this.counterEl = this.#own('[data-slideshow-counter]')[0];

    this.transition = this.dataset.transition || 'slide';
    this.loop = this.dataset.loop !== 'false';
    this.autoplaySpeed = Number.parseInt(this.dataset.autoplaySpeed, 10) || 5000;

    // A page opened in a background tab starts hidden, and no visibilitychange
    // arrives to say so, so the hold has to be taken here.
    this.#holds.clear();
    this.#watching.clear();
    if (document.hidden) this.#holds.add('hidden');

    this.#labelSlides();
    // Slide one can be switched off for this breakpoint, so start at the first
    // slide that is actually showing.
    const first = this.#visible[0] ?? this.#slides[0];
    this.#setActive(this.#slides.indexOf(first), 0, { animate: false });
    this.classList.add('slideshow-banner--ready');

    this.#bindEvents();

    if (this.dataset.autoplay === 'true' && this.#slides.length > 1) {
      this.#timer = new ResumableTimer(() => this.next(), this.autoplaySpeed);
      // Reduced motion means no movement the visitor did not ask for, so
      // autoplay starts stopped and the play button is left for them.
      if (REDUCED_MOTION.matches) this.#stopped = true;
      this.#applyDuration();
      this.#syncAutoplay();
      this.#restartProgress();
    }

    this.#updateToggleButton();
    this.#syncMedia();
    // A player that loaded before this script ran has already missed its
    // load event, so it is asked here to report when it is ready.
    this.#own('.slideshow-banner__blocks iframe').forEach((iframe) => this.#listenToEmbed(iframe));
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#timer?.stop();
  }

  /**
   * This slideshow's own elements. A slide can hold any theme block, including
   * another slideshow, whose slides and controls must not be mistaken for ours.
   */
  #own(selector) {
    return Array.from(this.querySelectorAll(selector)).filter((element) => element.closest('slideshow-banner') === this);
  }

  // -- Which slides count --------------------------------------------------

  /**
   * A slide can be switched off for one breakpoint. The stylesheet takes it out
   * of the layout; this keeps it out of the running order, the dots and the
   * slide count, so "Slide 2 of 3" stays true on a phone.
   */
  #isVisible(slide) {
    return MOBILE.matches ? !slide.hasAttribute('data-hide-mobile') : !slide.hasAttribute('data-hide-desktop');
  }

  get #visible() {
    return this.#slides.filter((slide) => this.#isVisible(slide));
  }

  /** Index into #slides of the slide `delta` steps away, or -1 if there is none. */
  #step(delta) {
    const visible = this.#visible;
    if (visible.length < 2) return -1;

    const here = visible.indexOf(this.#slides[this.#current]);
    if (here === -1) return this.#slides.indexOf(visible[0]);

    const next = here + delta;
    if (!this.loop && (next < 0 || next >= visible.length)) return -1;
    return this.#slides.indexOf(visible[(next + visible.length) % visible.length]);
  }

  #durationFor(slide) {
    const own = Number.parseInt(slide?.dataset.duration ?? '', 10);
    return Number.isFinite(own) && own > 0 ? own : this.autoplaySpeed;
  }

  /** Points the timer and the progress bar at the current slide's own length. */
  #applyDuration() {
    const ms = this.#durationFor(this.#slides[this.#current]);
    this.#timer?.setDuration(ms);
    this.style.setProperty('--sb-autoplay-duration', ms + 'ms');
  }

  // -- Setup ---------------------------------------------------------------

  #label(position, length) {
    const template = this.dataset.slideStatus || 'Slide [index] of [length]';
    return template.replace('[index]', String(position)).replace('[length]', String(length));
  }

  #labelSlides() {
    const visible = this.#visible;
    this.#slides.forEach((slide) => {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      const position = visible.indexOf(slide);
      if (position === -1) slide.removeAttribute('aria-label');
      else slide.setAttribute('aria-label', this.#label(position + 1, visible.length));
    });
  }

  #bindEvents() {
    const { signal } = this.#abort;

    this.#own('[data-slideshow-prev]').forEach((button) =>
      button.addEventListener('click', () => this.previous(), { signal })
    );
    this.#own('[data-slideshow-next]').forEach((button) =>
      button.addEventListener('click', () => this.next(), { signal })
    );
    this.playToggle?.addEventListener('click', () => this.toggleAutoplay(), { signal });

    this.#indicators.forEach((indicator) => {
      indicator.addEventListener(
        'click',
        () => this.goTo(Number.parseInt(indicator.dataset.slideshowGoto, 10)),
        { signal }
      );
    });

    // Deliberately no pause on hover. The owner wants the interval to run
    // continuously and stop only when the visitor presses pause, so resting
    // the pointer over the banner must not halt it.

    // Autoplay must not move content out from under a keyboard visitor, but
    // clicking an arrow with a mouse also focuses it, and holding on that
    // would leave the slideshow stopped until they clicked elsewhere.
    // :focus-visible is what separates the two.
    this.addEventListener(
      'focusin',
      (event) => {
        if (event.target.matches?.(':focus-visible')) this.#hold('focus');
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.#release('focus');
      },
      { signal }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.#hold('hidden');
        else this.#release('hidden');
      },
      { signal }
    );

    if (this.dataset.keyboard !== 'false') {
      this.addEventListener('keydown', this.#onKeydown, { signal });
    }

    if (this.dataset.swipe !== 'false' && this.slidesEl) {
      this.slidesEl.addEventListener('pointerdown', this.#onPointerDown, { signal });
      this.slidesEl.addEventListener('pointerup', this.#onPointerUp, { signal });
      this.slidesEl.addEventListener(
        'pointercancel',
        () => {
          this.#pointerId = null;
        },
        { signal }
      );
      // Dragging a photo or a link with the mouse starts the browser's own
      // drag of it, which cancels the swipe, so photo and linked slides
      // could not be swiped the way a plain one can.
      this.slidesEl.addEventListener('dragstart', this.#onDragStart, { signal });
      // Once that drag is out of the way, the click that ends a mouse swipe
      // would follow the slide's link.
      this.slidesEl.addEventListener('click', this.#onSwipeClick, { capture: true, signal });
    }

    REDUCED_MOTION.addEventListener('change', this.#onReducedMotionChange, { signal });
    MOBILE.addEventListener('change', this.#onBreakpointChange, { signal });

    // In the theme editor, selecting a slide in the sidebar, or anything
    // inside one, should bring that slide forward instead of leaving the
    // merchant editing something unseen.
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    document.addEventListener('shopify:block:deselect', this.#onBlockDeselect, { signal });

    // A video the visitor starts holds the slideshow until it stops. What
    // makes a start theirs is a press on that video, its poster or its
    // player just before it: autoplay, and the slideshow starting a video
    // again when its slide comes back, have none.
    const remember = (event) => {
      this.#lastPress = { target: event.target, time: performance.now() };
    };
    for (const type of ['pointerdown', 'click', 'keydown']) {
      this.addEventListener(type, remember, { capture: true, signal });
    }
    // A click inside a player never reaches the page, but it takes focus
    // away from the page and into the player.
    window.addEventListener('blur', this.#onWindowBlur, { signal });
    // Media events do not bubble, but they still pass through the slideshow
    // on their way down to the video.
    this.addEventListener('play', this.#onMediaPlay, { capture: true, signal });
    for (const type of ['pause', 'ended', 'emptied']) {
      this.addEventListener(type, this.#onMediaStop, { capture: true, signal });
    }

    // Embedded players are only ready for messages once they have loaded.
    // `load` does not bubble, so it is caught on the way down.
    this.addEventListener('load', this.#onEmbedLoad, { capture: true, signal });
    window.addEventListener('message', this.#onEmbedMessage, { signal });
  }

  // -- Navigation ----------------------------------------------------------

  next(options = {}) {
    const target = this.#step(1);
    if (target !== -1) this.goTo(target, { direction: 1, ...options });
  }

  previous(options = {}) {
    const target = this.#step(-1);
    if (target !== -1) this.goTo(target, { direction: -1, ...options });
  }

  goTo(index, options = {}) {
    const slide = this.#slides[index];
    if (Number.isNaN(index) || !slide) return;
    if (!this.#isVisible(slide)) return;
    if (index !== this.#current) {
      const direction = options.direction ?? (index > this.#current ? 1 : -1);
      this.#setActive(index, direction, { animate: true });
    }

    // Every move restarts the clock: the new slide gets a full interval
    // rather than the remainder of the old one. This is also what re-arms
    // autoplay, because the timer underneath is a single timeout that has
    // just spent itself. Going through syncAutoplay rather than resuming
    // directly is what keeps a paused slideshow paused. Pressing the dot of
    // the slide already showing restarts it too, like any other control.
    this.#timer?.stop();
    this.#applyDuration();
    this.#syncAutoplay();
    this.#restartProgress();
  }

  toggleAutoplay() {
    // While a video the visitor started holds the slideshow, the button shows
    // play, and pressing it is play: the slides run again and the video
    // carries on until its slide leaves.
    if (this.#stopped || this.#holds.has('video')) {
      this.#stopped = false;
      this.#watching.clear();
      this.#holds.delete('video');
    } else {
      this.#stopped = true;
    }

    // Pause and play both reset the countdown rather than banking whatever
    // was left of it, so a slide always gets its full turn from the moment
    // the visitor last touched the controls. stop() is what clears the
    // remainder; syncAutoplay then starts it again only if it should run.
    this.#timer?.stop();
    this.#syncAutoplay();
    this.#restartProgress();
    this.#updateToggleButton();
    this.#syncMedia();
  }

  // -- State ---------------------------------------------------------------

  #setActive(index, direction, { animate }) {
    const outgoing = this.#slides[this.#current];
    const incoming = this.#slides[index];

    // Anything playing in the slide that is leaving is paused below. Players
    // only confirm that when the message reaches them, so their hold is not
    // left waiting on it. The caller restarts the interval.
    for (const media of this.#watching) {
      if (!incoming.contains(media)) this.#watching.delete(media);
    }
    if (this.#watching.size === 0 && this.#holds.delete('video')) this.#updateToggleButton();
    clearTimeout(this.#awaitTimer);
    this.#holds.delete('player');

    this.#slides.forEach((slide) => {
      slide.classList.remove('is-active', 'is-leaving');
      slide.style.removeProperty('--sb-slide-offset');
      slide.setAttribute('aria-hidden', 'true');
      slide.inert = true;
    });

    const moving = animate && this.transition === 'slide' && !REDUCED_MOTION.matches;

    if (moving && outgoing && outgoing !== incoming) {
      outgoing.classList.add('is-leaving');
      outgoing.style.setProperty('--sb-slide-offset', direction > 0 ? '-100%' : '100%');
    }

    if (moving) {
      // Park the incoming slide off-screen with no transition, then let it
      // animate home. Reading offsetWidth flushes the parked position so the
      // browser animates from there rather than jumping straight to zero.
      incoming.classList.add('is-entering');
      incoming.style.setProperty('--sb-slide-offset', direction > 0 ? '100%' : '-100%');
      void incoming.offsetWidth;
      incoming.classList.remove('is-entering');
      incoming.style.removeProperty('--sb-slide-offset');
    }

    incoming.classList.add('is-active');
    incoming.setAttribute('aria-hidden', 'false');
    incoming.inert = false;

    this.#current = index;
    this.#adoptSlideColor(incoming);
    this.#updateIndicators();
    this.#announce();
    this.#syncMedia();
  }

  /**
   * Dots over a slide have to read against whatever is behind them, and that
   * changes per slide. The merchant already picked a text colour that contrasts
   * with each slide, so the controls borrow it unless a colour was set for them.
   */
  #adoptSlideColor(slide) {
    const color = getComputedStyle(slide).getPropertyValue('--sb-text-color').trim();
    if (color && color !== 'inherit') this.style.setProperty('--sb-controls-auto-color', color);
    // A slide without a colour of its own takes the section's text colour, and
    // so do the controls. Keeping the last slide's colour instead left white
    // dots on a pale slide.
    else this.style.removeProperty('--sb-controls-auto-color');
  }

  #updateIndicators() {
    const visible = this.#visible;

    this.#indicators.forEach((indicator, index) => {
      const slide = this.#slides[index];
      const position = slide ? visible.indexOf(slide) : -1;

      indicator.hidden = position === -1;
      if (position !== -1) {
        indicator.setAttribute('aria-label', this.#label(position + 1, visible.length));
        // The numbers style prints its own position, which shifts when a
        // slide is switched off for this breakpoint.
        const number = indicator.firstElementChild;
        if (number) number.textContent = String(position + 1);
      }

      const active = index === this.#current;
      indicator.classList.toggle('is-active', active);
      indicator.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (this.counterEl) {
      const position = visible.indexOf(this.#slides[this.#current]);
      this.counterEl.textContent = `${position + 1} / ${visible.length}`;
    }
  }

  #announce() {
    if (!this.liveRegion) return;
    this.liveRegion.textContent = this.#slides[this.#current]?.getAttribute('aria-label') ?? '';
  }

  // -- Autoplay ------------------------------------------------------------

  #hold(reason) {
    this.#holds.add(reason);
    this.#syncAutoplay();
  }

  #release(reason) {
    this.#holds.delete(reason);
    this.#syncAutoplay();
  }

  // Reduced motion is not checked here. It starts the slideshow stopped, and
  // checking it again would make the play button do nothing for exactly the
  // visitors it was left in place for.
  get #shouldRun() {
    return !this.#stopped && this.#holds.size === 0;
  }

  #syncAutoplay() {
    if (!this.#timer) return;

    if (this.#shouldRun) {
      this.#timer.resume();
      if (this.progressEl) this.progressEl.style.animationPlayState = 'running';
      this.#prepareNextMedia();
    } else {
      this.#timer.pause();
      if (this.progressEl) this.progressEl.style.animationPlayState = 'paused';
    }

    // Announcements are noise while slides advance on their own, and useful
    // once the visitor is the one moving them.
    this.liveRegion?.setAttribute('aria-live', this.#shouldRun ? 'off' : 'polite');
  }

  #restartProgress() {
    if (!this.progressEl) return;
    this.progressEl.classList.remove('is-running');
    void this.progressEl.offsetWidth;
    this.progressEl.classList.add('is-running');
    this.progressEl.style.animationPlayState = this.#shouldRun ? 'running' : 'paused';
  }

  /**
   * The button says what it will do, "Pause slideshow" or "Play slideshow".
   * It is not also marked pressed: "Play slideshow, pressed" contradicts itself.
   */
  #updateToggleButton() {
    if (!this.playToggle) return;
    // A video the visitor is watching holds the slides, so the button shows
    // they are not moving, and pressing it starts them.
    const paused = this.#stopped || this.#holds.has('video');
    this.playToggle.setAttribute(
      'aria-label',
      paused ? this.dataset.labelPlay || 'Play slideshow' : this.dataset.labelPause || 'Pause slideshow'
    );
    this.playToggle.classList.toggle('is-paused', paused);
  }

  // -- Media ---------------------------------------------------------------

  /**
   * A background video is motion the visitor did not ask for, so it stops
   * with the pause button, their one control over motion on the banner, and
   * waits for play when they asked for reduced motion. Without autoplay there
   * is no pause button, so reduced motion alone decides.
   */
  get #videoAllowed() {
    return this.#timer ? !this.#stopped : !REDUCED_MOTION.matches;
  }

  /** Only the showing slide's media plays. */
  #syncMedia() {
    const allowed = this.#videoAllowed;

    this.#slides.forEach((slide, index) => {
      const showing = index === this.#current;

      slide.querySelectorAll(':scope > .slideshow-banner__media video').forEach((video) => {
        if (showing && allowed) video.play()?.catch(() => {});
        else video.pause();
      });

      if (showing) {
        this.#resumeAddedMedia(slide);
        // A box switched off for this screen size is out of sight too.
        slide.querySelectorAll('[data-slideshow-box]').forEach((box) => {
          if (this.#hiddenBox(box)) this.#pauseAddedMedia(box);
        });
      } else {
        this.#pauseAddedMedia(slide);
      }
    });
  }

  /** Whether this is, or is inside, a free-position box switched off at this width. */
  #hiddenBox(element) {
    const box = element.closest('[data-slideshow-box]');
    return !!box && getComputedStyle(box).display === 'none';
  }

  /**
   * Videos after the first preload nothing, so one would only start
   * downloading when its slide shows and open on a still frame. Whenever the
   * slides are advancing on their own, the slide due next gets its videos
   * loading in the meantime. Called from #syncAutoplay, so every move, play
   * press and released hold keeps it one slide ahead.
   */
  #prepareNextMedia() {
    if (!this.#shouldRun || !this.#videoAllowed) return;
    const next = this.#slides[this.#step(1)];
    if (!next) return;

    const videos = [
      ...next.querySelectorAll(':scope > .slideshow-banner__media video'),
      ...next.querySelectorAll('.slideshow-banner__blocks video[data-slideshow-resume]'),
    ];
    videos.forEach((video) => {
      // One in a box switched off at this width will not be shown.
      if (video.preload !== 'none' || this.#hiddenBox(video)) return;
      video.preload = 'auto';
      video.load();
    });
  }

  /**
   * Video blocks added to a slide stop when it leaves. Those playing by
   * themselves, or set to, start again when it comes back; one the visitor
   * started waits for them to press play again.
   * @param {Element} root - a slide, or a box switched off at this width
   */
  #pauseAddedMedia(root) {
    root.querySelectorAll('.slideshow-banner__blocks video').forEach((video) => {
      const theirs = this.#startedByVisitor.has(video);
      // One set to play by itself may not have started yet, and would then
      // start out of sight. Pausing it first cancels that. The theme's
      // poster button writes autoplay on the video it adds, so that alone
      // does not say a video plays by itself.
      const waiting = video.autoplay && video.played.length === 0 && !theirs;
      if ((!video.paused && !theirs) || waiting) video.dataset.slideshowResume = '';
      video.pause();
      // Autoplay also starts the whole file downloading. Reloading it with
      // nothing preloaded stops that until the slide comes round and plays it.
      if (waiting) {
        video.autoplay = false;
        video.preload = 'none';
        video.load();
      }
    });

    this.#addedEmbeds(root).forEach((iframe) => {
      // A player set to play by itself comes back with its slide, as long as
      // it was still playing by itself. One the visitor started or paused
      // stays where they left it, like a video.
      const wrapper = iframe.closest('deferred-media');
      const autoplays = wrapper ? wrapper.hasAttribute('autoplay') : /[?&]autoplay=1(&|$)/.test(iframe.src);
      const embed = this.#embeds.get(iframe);
      const resumes = embed?.started ? embed.playing && !this.#startedByVisitor.has(iframe) : autoplays;
      if (resumes) iframe.dataset.slideshowResume = '';
      this.#commandEmbed(iframe, false);
    });
  }

  #resumeAddedMedia(slide) {
    slide.querySelectorAll('.slideshow-banner__blocks video[data-slideshow-resume]').forEach((video) => {
      if (this.#hiddenBox(video)) return;
      delete video.dataset.slideshowResume;
      // The play event this causes is the slideshow's, not the visitor's.
      if (video.paused) this.#startedBySlideshow.add(video);
      video.play()?.catch(() => this.#startedBySlideshow.delete(video));
    });

    this.#addedEmbeds(slide).forEach((iframe) => {
      if (this.#hiddenBox(iframe)) return;
      if (iframe.hasAttribute('data-slideshow-resume')) {
        delete iframe.dataset.slideshowResume;
        this.#commandEmbed(iframe, true);
      } else {
        delete iframe.dataset.slideshowWants;
      }
    });
  }

  // -- Embedded players ------------------------------------------------------

  /** YouTube and Vimeo players placed in a slide, not in a slideshow nested inside it. */
  #addedEmbeds(root) {
    return Array.from(root.querySelectorAll('.slideshow-banner__blocks iframe')).filter(
      (iframe) => iframe.closest('slideshow-banner') === this && embedKind(iframe)
    );
  }

  /**
   * Asks an embedded player to play or pause. The request is remembered, so
   * it can be repeated once the player is ready: one still loading misses any
   * message sent to it, and would otherwise start playing out of sight.
   */
  #commandEmbed(iframe, play) {
    const kind = embedKind(iframe);
    if (!kind) return;
    iframe.dataset.slideshowWants = play ? 'play' : 'pause';
    // A start soon after this is the slideshow's doing, not the visitor's.
    this.#embed(iframe).asked = play ? performance.now() : 0;
    const message =
      kind === 'youtube'
        ? { event: 'command', func: play ? 'playVideo' : 'pauseVideo', args: '' }
        : { method: play ? 'play' : 'pause' };
    iframe.contentWindow?.postMessage(JSON.stringify(message), new URL(iframe.src).origin);
  }

  /**
   * Asks a player to report when it starts and stops. YouTube only says it
   * is ready, and then reports its state, once the page says it is
   * listening. Vimeo reports each event it is asked for.
   */
  #listenToEmbed(iframe) {
    const kind = embedKind(iframe);
    if (!kind) return;
    const origin = new URL(iframe.src).origin;
    const post = (message) => iframe.contentWindow?.postMessage(JSON.stringify(message), origin);
    if (kind === 'vimeo') {
      for (const value of ['play', 'pause', 'ended']) post({ method: 'addEventListener', value });
      return;
    }

    const say = () => {
      post({ event: 'listening', id: 'slideshow-banner', channel: 'widget' });
      post({ event: 'command', func: 'addEventListener', args: ['onStateChange'], id: 'slideshow-banner', channel: 'widget' });
    };
    say();
    // The player's own script can start listening after the frame has
    // loaded, and a message sent before then is lost: in 2 of 12 test loads
    // the player never answered. YouTube's own API says it again until the
    // player answers, and so does this, for up to ten seconds.
    const embed = this.#embed(iframe);
    if (embed.heard || embed.calling) return;
    embed.calling = true;
    let tries = 0;
    const retry = setInterval(() => {
      if (embed.heard || !iframe.isConnected || this.#abort?.signal.aborted || ++tries > 40) {
        clearInterval(retry);
        embed.calling = false;
        return;
      }
      say();
    }, 250);
  }

  /** What the slideshow knows about a player, created on first use. */
  #embed(iframe) {
    let embed = this.#embeds.get(iframe);
    if (!embed) {
      embed = { playing: false, started: false, asked: 0, heard: false, calling: false };
      this.#embeds.set(iframe, embed);
    }
    return embed;
  }

  /**
   * A player the visitor starts holds the slideshow as a video does. The
   * page never sees a click inside a player, but that click moves focus into
   * it, and the theme's poster button focuses the player it adds. A start
   * the slideshow asked for, or the player's own autoplay, has neither.
   */
  #onEmbedState(iframe, playing) {
    const embed = this.#embed(iframe);
    if (playing === embed.playing) return;
    embed.playing = playing;

    if (!playing) {
      this.#stopWatching(iframe);
      return;
    }

    embed.started = true;
    const asked = embed.asked && performance.now() - embed.asked < ASK_WINDOW;
    embed.asked = 0;
    const theirs = !asked && (document.activeElement === iframe || this.#pressedJustNow(iframe));
    if (theirs) {
      this.#startedByVisitor.add(iframe);
      this.#startWatching(iframe);
    } else {
      this.#startedByVisitor.delete(iframe);
    }
  }

  #repeatEmbedRequest(iframe) {
    const wants = iframe.dataset.slideshowWants;
    if (wants) this.#commandEmbed(iframe, wants === 'play');
  }

  #onEmbedLoad = (event) => {
    const iframe = event.target;
    if (!(iframe instanceof HTMLIFrameElement) || iframe.closest('slideshow-banner') !== this) return;
    if (!embedKind(iframe)) return;
    // A frame that loads again has a new player, which has not heard yet.
    this.#embed(iframe).heard = false;
    this.#listenToEmbed(iframe);
    this.#repeatEmbedRequest(iframe);
  };

  /** YouTube announces `onReady`, Vimeo `ready`. Either way, repeat the last request. */
  #onEmbedMessage = (event) => {
    const iframe = this.#own('.slideshow-banner__blocks iframe').find((frame) => frame.contentWindow === event.source);
    if (!iframe || !embedKind(iframe) || event.origin !== new URL(iframe.src).origin) return;

    let data = event.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        return;
      }
    }
    this.#embed(iframe).heard = true;
    if (data?.event === 'onReady' || data?.event === 'ready') {
      // Vimeo only takes requests to report its events once it is ready.
      if (data.event === 'ready') this.#listenToEmbed(iframe);
      this.#repeatEmbedRequest(iframe);
      return;
    }

    const playing = embedPlaying(data);
    if (playing !== null) {
      this.#onEmbedState(iframe, playing);
      // Either way the player has said: the short wait is over.
      this.#stopAwaiting();
    } else if (embedBuffering(data) && document.activeElement === iframe) {
      // Play pressed again inside a player that already has focus.
      this.#awaitPlayer(iframe);
    }
  };

  /**
   * The visitor clicked into a player. A player takes most of a second to say
   * it is playing, and a slide due to move in that time would move and pause
   * it, so the slides wait until it says, for a few seconds at most. If it
   * does not play, the countdown carries on where it stopped.
   */
  #onWindowBlur = () => {
    const iframe = document.activeElement;
    if (iframe instanceof HTMLIFrameElement && embedKind(iframe) && this.#isAdded(iframe)) this.#awaitPlayer(iframe);
  };

  #awaitPlayer(iframe) {
    if (!this.#slides[this.#current]?.contains(iframe) || this.#embed(iframe).playing) return;
    clearTimeout(this.#awaitTimer);
    this.#awaitTimer = setTimeout(() => this.#stopAwaiting(), AWAIT_WINDOW);
    if (!this.#holds.has('player')) this.#hold('player');
  }

  #stopAwaiting() {
    clearTimeout(this.#awaitTimer);
    if (this.#holds.has('player')) this.#release('player');
  }

  // -- Videos the visitor plays ----------------------------------------------

  /** A video or player placed in one of this slideshow's slides. */
  #isAdded(media) {
    return media.closest('slideshow-banner') === this && !!media.closest('.slideshow-banner__blocks');
  }

  /** Whether the visitor's last press, just now, was on this media or its poster. */
  #pressedJustNow(media) {
    const { target, time } = this.#lastPress;
    if (!target || performance.now() - time > PRESS_WINDOW) return false;
    if (media === target || media.contains(target)) return true;
    const wrapper = media.closest('deferred-media');
    return !!wrapper && wrapper.contains(target);
  }

  #onMediaPlay = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLMediaElement) || !this.#isAdded(video)) return;

    if (this.#startedBySlideshow.has(video)) {
      this.#startedBySlideshow.delete(video);
      this.#startedByVisitor.delete(video);
      return;
    }
    if (!this.#pressedJustNow(video) && !this.#usingControls(video)) {
      this.#startedByVisitor.delete(video);
      return;
    }
    this.#startedByVisitor.add(video);
    this.#startWatching(video);
  };

  /**
   * The browser's own video controls send the page no event when they are
   * pressed, but pressing them moves focus to the video and counts as the
   * visitor acting. Neither is true of a video playing by itself.
   */
  #usingControls(video) {
    return document.activeElement === video && navigator.userActivation?.isActive !== false;
  }

  #onMediaStop = (event) => {
    if (event.target instanceof HTMLMediaElement) this.#stopWatching(event.target);
  };

  /** The visitor started something in the showing slide: hold until it stops. */
  #startWatching(media) {
    if (!this.#slides[this.#current]?.contains(media)) return;
    this.#watching.add(media);
    if (this.#holds.has('video')) return;
    this.#hold('video');
    this.#updateToggleButton();
  }

  /**
   * It stopped, for whatever reason. Once nothing the visitor started is
   * playing, the slide gets a full interval from now rather than what was
   * left, so it does not move on the moment they pause.
   */
  #stopWatching(media) {
    if (!this.#watching.delete(media) || this.#watching.size > 0) return;
    if (!this.#holds.delete('video')) return;
    this.#timer?.stop();
    this.#syncAutoplay();
    this.#restartProgress();
    this.#updateToggleButton();
  }

  // -- Input ---------------------------------------------------------------

  /** Jumps to the first (`0`) or last (`-1`) slide that is showing right now. */
  #goToEdge(edge, direction) {
    const visible = this.#visible;
    const slide = edge === 0 ? visible[0] : visible[visible.length - 1];
    if (slide) this.goTo(this.#slides.indexOf(slide), { direction });
  }

  #onKeydown = (event) => {
    // A slideshow placed inside a slide handles its own keys.
    if (event.target.closest('slideshow-banner') !== this) return;
    // Arrow keys already mean something in a form field, a media player's
    // controls or editable text added to a slide.
    if (event.target.matches('input, textarea, select, video, audio') || event.target.isContentEditable) return;

    switch (event.key) {
      case 'ArrowLeft':
        this.previous();
        break;
      case 'ArrowRight':
        this.next();
        break;
      case 'Home':
        this.#goToEdge(0, -1);
        break;
      case 'End':
        this.#goToEdge(-1, 1);
        break;
      default:
        return;
    }

    event.preventDefault();
  };

  #onPointerDown = (event) => {
    this.#swiped = false;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Dragging inside a nested slideshow, a form field or a video's controls
    // is not a swipe of this slideshow.
    if (event.target.closest('slideshow-banner') !== this) return;
    if (event.target.closest('input, textarea, select, video, audio')) return;
    this.#pointerId = event.pointerId;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  #onPointerUp = (event) => {
    if (this.#pointerId !== event.pointerId || !this.#pointerStart) return;
    const dx = event.clientX - this.#pointerStart.x;
    const dy = event.clientY - this.#pointerStart.y;
    this.#pointerId = null;
    this.#pointerStart = null;

    // A mostly-vertical drag is the visitor scrolling the page, not swiping.
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;

    // The click that follows this pointerup is part of the swipe. It arrives
    // before any timer, so only that one is swallowed.
    this.#swiped = true;
    setTimeout(() => {
      this.#swiped = false;
    });

    if (dx < 0) this.next();
    else this.previous();
  };

  #onDragStart = (event) => {
    if (this.#pointerId !== null) event.preventDefault();
  };

  #onSwipeClick = (event) => {
    if (!this.#swiped) return;
    this.#swiped = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Crossing the breakpoint can switch slides on or off, which changes the
   * numbering and can leave the showing slide hidden.
   */
  #onBreakpointChange = () => {
    this.#labelSlides();

    const current = this.#slides[this.#current];
    if (current && !this.#isVisible(current)) {
      const first = this.#visible[0];
      if (first) this.goTo(this.#slides.indexOf(first), { direction: 1 });
    }

    this.#updateIndicators();
    this.#announce();

    // Crossing the breakpoint can bring slides back into play. If there had
    // been nothing to advance to, next() returned without re-arming the timer,
    // so autoplay needs starting again rather than resuming.
    this.#timer?.stop();
    this.#applyDuration();
    this.#syncAutoplay();
    // The bar shows the same fresh interval, rather than filling up early and
    // sitting full until the slide moves.
    this.#restartProgress();
    // A box can be switched on or off at this width, with a video in it.
    this.#syncMedia();
  };

  #onReducedMotionChange = () => {
    // Asking for reduced motion part-way through a visit stops the slides as
    // if pause had been pressed. Turning it off leaves them stopped until the
    // visitor presses play.
    if (REDUCED_MOTION.matches && this.#timer) this.#stopped = true;
    this.#timer?.stop();
    this.#syncAutoplay();
    this.#restartProgress();
    this.#updateToggleButton();
    this.#syncMedia();
  };

  /** The selected block can be the slide itself or anything placed inside it. */
  #onBlockSelect = (event) => {
    const index = this.#slides.findIndex((slide) => slide.contains(event.target));
    if (index === -1) return;
    this.#editorSelection = event.target;
    this.#hold('editor');
    this.goTo(index);
  };

  /**
   * Moving between two blocks in one slide deselects the first. Releasing only
   * for the block that is actually still selected keeps the hold in place
   * whichever order the editor sends the two events in.
   */
  #onBlockDeselect = (event) => {
    if (event.target !== this.#editorSelection) return;
    this.#editorSelection = null;
    this.#release('editor');
  };
}

if (!customElements.get('slideshow-banner')) {
  customElements.define('slideshow-banner', SlideshowBanner);
}
